import React, { useEffect, useRef, useState } from 'react';
import M from 'materialize-css';
import './Endorsements.scss';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import LicenceActions, {
   getLicenceData,
   isFetchingLicence,
} from 'pgdb-data-layer/lib/Redux/LicenceRedux';
import UserActions, {
   getUserInfoData,
} from 'pgdb-data-layer/lib/Redux/UserInfoRedux';
import { getToken } from '../../../Session/SessionRedux';
import Loading from '../../../Components/Loading';
import Callout from '../../../Components/Callout';
import ExternalLink from '../../../Components/ExternalLink';
import Navigator from '../../../Navigation/Navigator';
import ActiveEndorsements from '../../../Components/ActiveEndorsements';
import { EndorsementTimeLimitNotice } from './EndorsementTimeLimitNotice';
import EndorsementBadge from '../../../Components/EndorsementBadge';
import LicenceCardFront from '../../../Components/LicenceIdCard/LicenceCardFront';
import LicenceCardBack from '../../../Components/LicenceIdCard/LicenceCardBack';
import * as PROFILE_HELPERS from 'pgdb-data-layer/lib/Helpers/Profile';
import { createPersonCardDto } from '../Licences';
import * as REGISTRATION from 'pgdb-data-layer/lib/Constants/Registration';
import EndorsementActions, {
   getEndorsement,
   isFetchingEndorsement,
} from 'pgdb-data-layer/lib/Redux/EndorsementRedux';
import ApplicationsAction, {
   isFetchingApplication,
   isFetchingApplicationSuccess,
} from 'pgdb-data-layer/lib/Redux/ApplicationsRedux';
import CourseActions, {
   getActiveEndorsementCourseData,
} from 'pgdb-data-layer/lib/Redux/CoursesRedux';
import { LICENCE_STATUS_ACTIVE } from '../Shared/StatusCodes';
import {
   APPLICATION_STATUS_PENDING_TRAINING,
   APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT,
} from 'pgdb-data-layer/lib/Constants/Contact';
import { PASS_CODE } from 'pgdb-data-layer/lib/Constants/Course';
import {
   ENDORSEMENT_TYPE_PLUMBER,
   ENDORSEMENT_TYPE_DRAINLAYER,
} from 'pgdb-data-layer/lib/Helpers/Endorsement';

const training_result_poll_base_ms = 3000;
const training_result_poll_base_max_ms = 5000;
const training_result_poll_max_backoff_ms = 30000;

// calculates delay with exponential backoff and random delay
// to avoid overwhelming the server in case of many users polling at the same time
const getPollDelay = (attempt) => {
   const base = training_result_poll_base_ms + Math.random() * (training_result_poll_base_max_ms - training_result_poll_base_ms);
   return Math.min(base * Math.pow(2, attempt), training_result_poll_max_backoff_ms);
};

const Endorsements = ({
   token,
   licences,
   isLoading,
   isFetchingEndorsementData,
   getLicences,
   endorsement,
   activeCourse,
   deleteSuccess,
   saveApplicationSuccess,
   patchApplicationSuccess,
   getUserEndorsement,
   getActiveEndorsementCourse,
   patchApplication,
   contactData,
   getUserContactInfo,
}) => {

   const modalEl = useRef(null);
   const [isModalOpen, setIsModalOpen] = useState(false);

   // used refs to survive re-renders
   const pollingTimeoutRef = useRef(null);
   const inflightRef = useRef(false);
   const attemptRef = useRef(0);

   useEffect(() => {
      if (!isFetchingEndorsementData) {
         inflightRef.current = false;
      }
   }, [isFetchingEndorsementData]);

   useEffect(() => {
      const hasPendingTraining =
         endorsement &&
         endorsement.applicationEndorsements &&
         endorsement.applicationEndorsements.some(
            a => a.applicationStatusCode === APPLICATION_STATUS_PENDING_TRAINING
         );

      clearTimeout(pollingTimeoutRef.current);

      // don't do any polling if there is no training in progress
      if (!hasPendingTraining) {
         attemptRef.current = 0;
         return;
      }

      // poll only if:
      // - there is a pending training application
      // - there is no inflight request to avoid multiple simultaneous requests
      const delay = getPollDelay(attemptRef.current);
      pollingTimeoutRef.current = setTimeout(() => {
         if (inflightRef.current) return;
         inflightRef.current = true;
         attemptRef.current += 1;
         getUserEndorsement(token);
      }, delay);

      return () => clearTimeout(pollingTimeoutRef.current);
   }, [endorsement, token]);

   useEffect(() => {
      if (token) {
         if (!licences.currentLicences || licences.currentLicences.length === 0) {
            getLicences(token);
         }
         getUserEndorsement(token);
         getUserContactInfo(token);
         getActiveEndorsementCourse(token);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [token]);

   useEffect(() => {
      // return immediately if: 
      // - no active endorsement 
      // - no active course or result
      // - no application at all
      if (!activeCourse || !activeCourse.course || !activeCourse.result) return;
      if (!endorsement || !endorsement.applicationEndorsements) return;

      const passedTraining = activeCourse.result.some(
         r => r.courseId === activeCourse.course.courseId && r.passFailCode === PASS_CODE
      );

      // return immediately if:
      // - no passing result for the active course
      if (!passedTraining) return;

      // check if course corresponds to any pending training applications, 
      // and if so, patch to update status to pending payment
      endorsement.applicationEndorsements.forEach(app => {
         if (app.applicationStatusCode === APPLICATION_STATUS_PENDING_TRAINING) {
            patchApplication(token, app.applicationId, { ApplicationStatusCode: APPLICATION_REGISTRATION_ON_HOLD_PENDING_PAYMENT });
         }
      });
   }, [activeCourse, endorsement]);

   const [canApplyEndorsement, setCanApplyEndorsement] = React.useState(false);

   const hasEndorsement = (endorsements, code) => {
      return endorsements.some(e => e.endorsementTypeCode === code);
   };

   const isCertifyingLicence = (currentLicences, code) => {
      return currentLicences.some(
         licence => licence.typeCode === code && licence.statusCode === LICENCE_STATUS_ACTIVE
      );
   };

   useEffect(() => {
      if (deleteSuccess || saveApplicationSuccess || patchApplicationSuccess) {
         getUserEndorsement(token);
      }
   }, [deleteSuccess, getUserEndorsement, saveApplicationSuccess, patchApplicationSuccess, token]);

   useEffect(() => {
      if (licences && licences.currentLicences && licences.currentLicences.length > 0) {
         const hasRenewalEligible =
            endorsement.endorsements &&
            endorsement.endorsements.some(e => e.canApplyForRenewal);
         let canApply =
            !hasRenewalEligible &&
            isCertifyingLicence(licences.currentLicences, REGISTRATION.CERTIFYING_PLUMBER_CODE) &&
            !hasEndorsement(endorsement.endorsements, ENDORSEMENT_TYPE_PLUMBER) &&
            endorsement.applicationEndorsements.length === 0;
         canApply =
            canApply ||
            (!hasRenewalEligible &&
               isCertifyingLicence(licences.currentLicences, REGISTRATION.CERTIFYING_DRAINLAYER_CODE) &&
               !hasEndorsement(endorsement.endorsements, ENDORSEMENT_TYPE_DRAINLAYER) &&
               endorsement.applicationEndorsements.length === 0);
         setCanApplyEndorsement(canApply);
      }
   }, [endorsement, licences]);

   const hasCurrentLicences =
      licences.currentLicences != null && licences.currentLicences.length > 0;

   const personCard =
      hasCurrentLicences && contactData
         ? createPersonCardDto(
              contactData.firstName,
              contactData.lastName,
              PROFILE_HELPERS.getUserContactPhoto(contactData),
              contactData.registrationNumber || 0,
              licences.currentLicences,
              licences.currentData.expires,
              endorsement.activeEndorsements
           )
         : null;

   const openModal = () => {
      setIsModalOpen(true);
      const element = M.Modal.init(modalEl.current);
      element.open();
   };

   const displayBadgeEndorsement = () => {
      if (endorsement.endorsements && endorsement.endorsements.length > 0) {
         return (
            <EndorsementBadge
               endorsements={endorsement.endorsements}
               futureEndorsements={endorsement.futureEndorsements}
               applicationEndorsements={endorsement.applicationEndorsements}
               onViewLicence={personCard ? openModal : null}
            />
         );
      }
      return <></>;
   };

   return (
      <main>
         <Loading isLoading={isLoading}>
            <div className="section no-pad-bot" id="index-banner">
               <div className="container">
                  <div
                     ref={modalEl}
                     className="modal black-text licence-modal"
                  >
                     {personCard && isModalOpen && (
                        <>
                           <LicenceCardFront details={personCard} />
                           <br />
                           <LicenceCardBack details={personCard} />
                        </>
                     )}
                  </div>
                  <div className="row">
                     <div className="col s10 m10 l10 offset-l1">
                        <h4 className="columnheader">
                           Self-Certification Endorsement
                        </h4>
                     </div>
                  </div>
                  {(isCertifyingLicence(licences.currentLicences, REGISTRATION.CERTIFYING_PLUMBER_CODE) ||
                     isCertifyingLicence(licences.currentLicences, REGISTRATION.CERTIFYING_DRAINLAYER_CODE)) ? (
                     <>
                        <div className="row">
                           <div className="col s12 m10 l10 offset-l1">
                              <p className="grey2-text medium-text medium-font">
                                 You are eligible to apply for a Self-Certification Endorsment because you have an 
                                 active Certifying Plumber and/or Certifying Drainlayer licence.
                              </p>
                           </div>
                           <div className="col s12 m10 l10 offset-l1">
                              <EndorsementTimeLimitNotice detailed />
                           </div>
                           {canApplyEndorsement && (
                              <div className="row">
                                 <div className="col s10 m10 l10 offset-l1">
                                    <div className="endorsement-section">
                                       <button
                                          className="btn waves-effect waves-light pgdb-blue"
                                          onClick={() => Navigator.toApplyEndorsement()}
                                       >
                                          APPLY HERE
                                       </button>
                                    </div>
                                 </div>
                              </div>
                           )}
                        </div>
                        <ActiveEndorsements
                           applicationEndorsements={endorsement.applicationEndorsements}
                        />
                        {displayBadgeEndorsement()}
                        <div className="row">
                           <div className="col s10 m10 l10 offset-l1">
                              <Callout>
                                 <p>
                                    For more information, please visit the{' '}
                                    <ExternalLink href="https://www.pgdb.co.nz">
                                       www.pgdb.co.nz
                                    </ExternalLink>
                                    {' '}or contact us directly.
                                 </p>
                              </Callout>
                           </div>
                        </div>
                     </>
                  ) : (
                     <>
                        <div className="row">
                           <div className="col s10 m10 l10 offset-l1">
                              <p className="grey2-text medium-text medium-font">
                                 You are not eligible for a self certifying endorsement.
                              </p>
                           </div>
                        </div>
                        {displayBadgeEndorsement()}
                        <Callout>
                           <p>
                              To be eligible for a Self-Certification Endorsement, you must hold
                              an active Certifying Plumber or Certifying Drainlayer licence.
                           </p>
                           <p>
                              For more information, please visit the{' '}
                              <ExternalLink href="https://www.pgdb.co.nz">
                                 www.pgdb.co.nz
                              </ExternalLink>
                              {' '}or contact us directly.
                           </p>
                        </Callout>
                     </>
                  )}
               </div>
            </div>
         </Loading>
      </main>
   );
};

Endorsements.propTypes = {
   token: PropTypes.string.isRequired,
   licences: PropTypes.object.isRequired,
   isLoading: PropTypes.bool.isRequired,
   getLicences: PropTypes.func.isRequired,
   getUserEndorsement: PropTypes.func.isRequired,
   getUserContactInfo: PropTypes.func.isRequired,
   contactData: PropTypes.object,
   endorsement: PropTypes.object,
   activeCourse: PropTypes.object,
   isFetchingEndorsementData: PropTypes.bool.isRequired,
   getActiveEndorsementCourse: PropTypes.func.isRequired,
   patchApplication: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
   const isLoading =
      isFetchingLicence(state) ||
      isFetchingEndorsement('getUserEndorsement', state) ||
      isFetchingApplication('deleteApplication', state) ||
      isFetchingApplication('saveApplication', state) ||
      isFetchingApplication('patchApplication', state);

   return {
      isLoading,
      licences: getLicenceData(state),
      token: getToken(state),
      contactData: getUserInfoData(state.user),
      endorsement: getEndorsement(state).endorsement,
      deleteSuccess: isFetchingApplicationSuccess('deleteApplication', state),
      saveApplicationSuccess: isFetchingApplicationSuccess('saveApplication', state),
      patchApplicationSuccess: isFetchingApplicationSuccess('patchApplication', state),
      isFetchingEndorsementData: isFetchingEndorsement('getUserEndorsement', state) || false,
      activeCourse: getActiveEndorsementCourseData(state),
   };
};

const mapDispatchToProps = dispatch => ({
   getLicences: token => dispatch(LicenceActions.licenceRequest(token)),
   getUserEndorsement: token =>
      dispatch(EndorsementActions.getUserEndorsementRequest(token)),
   getUserContactInfo: token =>
      dispatch(UserActions.getUserContactInfoRequest(token)),
   getActiveEndorsementCourse: token =>
      dispatch(CourseActions.getActiveEndorsementCourseRequest(token)),
   patchApplication: (token, applicationId, data) =>
      dispatch(ApplicationsAction.patchApplicationRequest(token, applicationId, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Endorsements);
